import type { FormEvent } from 'react';
import { useState } from 'react';
import type { EarnPointsRequest } from '../../../../infrastructure/LoyaltyClient';
import { Modal, Form, Button } from 'react-bootstrap';

interface EarnPointsModalProps {
  show: boolean;
  onClose: () => void;
  onEarn: (points: number, activityType: EarnPointsRequest['activityType'], orderId?: string, subDescription?: string) => Promise<void>;
}

export const EarnPointsModal = ({ show, onClose, onEarn }: EarnPointsModalProps) => {
  const [points, setPoints] = useState('');
  const [activityType, setActivityType] = useState<EarnPointsRequest['activityType']>('Purchase');
  const [orderId, setOrderId] = useState('');
  const [subDescription, setSubDescription] = useState('');
  const subtitleTooLong = subDescription.trim().length > 200;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (subtitleTooLong || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onEarn(parseInt(points), activityType, orderId || undefined, subDescription.trim() || undefined);
      setPoints('');
      setActivityType('Purchase');
      setOrderId('');
      setSubDescription('');
    } catch {
      // The loyalty hook displays the error; preserve inputs without retrying the grant.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Earn Points</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Points</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter points to earn"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              required
              min="1"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Activity Type</Form.Label>
            <Form.Select value={activityType} onChange={(e) => setActivityType(e.target.value as EarnPointsRequest['activityType'])} required>
              <option value="Purchase">Purchase</option>
              <option value="Event">Event</option>
              <option value="Referral">Referral</option>
              <option value="RewardRedemption">Reward Redemption</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Order ID (Optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter related order ID"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="earn-points-subtitle">
            <Form.Label>Sub-title (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={subDescription}
              onChange={(e) => setSubDescription(e.target.value)}
              isInvalid={subtitleTooLong}
              aria-describedby="earn-points-subtitle-feedback"
            />
            <Form.Control.Feedback type="invalid" id="earn-points-subtitle-feedback">
              Sub-title must be 200 characters or fewer.
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={isSubmitting || subtitleTooLong}>
            {isSubmitting ? 'Processing...' : 'Earn Points'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
